import type { Card } from '@fanduel/braze-content-cards-inbox/types/Cards';
import { type PRODUCT_THEME, REGIONS } from '@/utils/constants';
import { PRODUCT_THROTTLES } from '@/utils/feature-flags/constants';
import { useGlobalContext } from '@/store/contexts/global-context';
import { getGameLaunchNavigationTarget } from '@/utils/url/game-launch-navigation-target';
import { AMPLITUDE_EXPERIMENTS, isExperimentEnabled } from '@/utils/experiments';

const POPUP_SIZES = { width: 1280, height: 720 };
const INCENTIVE_GAMES_RGI = 'incentive_games';

const getQueryParam = (url: string, key: string) => {
    try {
        const parsed = new URL(url);
        return parsed.searchParams.get(key);
    } catch {
        return null;
    }
};

const getRgiParam = (url: string) => getQueryParam(url, 'rgi') ?? getQueryParam(url, 'RGI');

const urlIsClgGame = (url: string) => {
    try {
        const parsed = new URL(url);
        return parsed.pathname.includes('/clg/') && parsed.searchParams.get('linkType') === 'game';
    } catch {
        return false;
    }
};

const isLauncherOrRMLink = (url = '') =>
    url.includes('launcher') || urlIsClgGame(url) || getRgiParam(url) === INCENTIVE_GAMES_RGI;

const addQueryParams = (url: string, params: Record<string, string>) => {
    try {
        const parsed = new URL(url);
        for (const [key, value] of Object.entries(params)) {
            parsed.searchParams.set(key, value);
        }
        return parsed.toString();
    } catch {
        return url;
    }
};

export const useNotificationBtn = (productTheme: PRODUCT_THEME) => {
    const { region, isMobile, featureFlags, experiments, isAndroidWrapper, isIosWrapper, isXsellWrapper } =
        useGlobalContext();
    const hasRM = featureFlags[PRODUCT_THROTTLES.REWARDS_MACHINE];
    const isRMLink = (url = '') => getRgiParam(url) === INCENTIVE_GAMES_RGI;

    const validateCard = (card: Card) => {
        return isRMLink(card.url ?? '') ? hasRM : true;
    };

    const getWindowFeatures = () => {
        if (isMobile) return undefined;

        const centerY = (window.screen.height - POPUP_SIZES.height) / 2;
        const centerX = (window.screen.width - POPUP_SIZES.width) / 2;
        return `toolbar=no,location=no,status=no,menubar=no,resizable=yes,top=${centerY},left=${centerX},width=${POPUP_SIZES.width},height=${POPUP_SIZES.height}`;
    };

    const handleClickOfCard = (url = '') => {
        let interimUrl = url;
        const params: Record<string, string> = {};
        const isMobileWebOrDesktop = !isAndroidWrapper && !isIosWrapper && !isXsellWrapper;

        const isGameLaunchInLobbyEnabled = isExperimentEnabled(experiments, AMPLITUDE_EXPERIMENTS.SAME_DOMAIN_LAUNCH);

        if (isMobileWebOrDesktop && isGameLaunchInLobbyEnabled) {
            const currentUrl = new URL(url);
            const sameDomainHostname = currentUrl.hostname.replace('launcher.', '');
            interimUrl = `https://${sameDomainHostname}/game-play${currentUrl.pathname}`;
        }

        if (isRMLink(interimUrl)) {
            if (!hasRM) {
                return;
            }
            params.theme = getQueryParam(interimUrl, 'theme') ?? productTheme;
            // TODO add this datadog event when RM finished
            // ddRumAction(ddActionRmGameLaunchedNotification(DD_USE_CASES.NOTIFICATION));
        }

        params.region = getQueryParam(interimUrl, 'region') ?? (region || REGIONS.MI);
        const finalUrl = addQueryParams(interimUrl, params);
        const target = getGameLaunchNavigationTarget(finalUrl, isMobile);
        const features = isLauncherOrRMLink(url) ? getWindowFeatures() : undefined;
        window.open(finalUrl, target, features);
    };

    return { validateCard, handleClickOfCard };
};
